import React from 'react';
import { Query } from 'react-apollo';
import { compose } from 'recompose';
import { compact, curry, get, isEmpty, map } from 'lodash';

import { getUnits } from 'utils/unit';

import DealerLocatorContext from '../DealerLocatorContext';
import withProximityFilters from '../withProximityFilters';

import searchAssetAreaGQL from './searchAssetAreaQuery';
import searchAssetRouteGQL from './searchAssetRouteQuery';

// https://servicesolutions.atlassian.net/browse/OQA-960
const pollInterval = 30 * 1000;

const getRoute = ({
  isRouteSearch,
  locationSearches: [{ location: firstLocation }, ...otherSearches],
}) =>
  isRouteSearch
    ? compact([firstLocation, ...map(otherSearches, 'location')])
    : undefined;

export function skipQuery({
  countryCode,
  isRouteSearch,
  locationSearches: [assetLocationSearch, ...destinationLocationSearches],
  proximityDistance,
}) {
  const { location: assetLocation } = assetLocationSearch;

  // these arguments are required for any dealer locator query:
  if (isEmpty(assetLocation) || !countryCode || !proximityDistance) {
    return true;
  }

  // searching along a route additionally requires at least one destination with a location
  // the nested if is to prevent running the extra logic on area searches.
  if (isRouteSearch) {
    const hasValidDestinationLocations = destinationLocationSearches
      .map(({ location }) => !isEmpty(location))
      .includes(true);

    if (!hasValidDestinationLocations) {
      return true;
    }
  }

  // we have what we need, _don't_ skip the query
  return false;
}

export function buildQueryVariables(componentProps) {
  const {
    encodedPolyline,
    inboundProgramNumber,
    locationSearches: [assetLocationSearch],
    proximityDistance: distance,
    selectedFeatureFilters,
    unit,
  } = componentProps;

  const { location: assetLocation, countryCode } = assetLocationSearch;
  const inboundProgramBillTo = get(inboundProgramNumber, 'billTo');
  const { distanceUnitValue } = getUnits(unit);

  return {
    assetLocation,
    countryCode,
    distance,
    distanceUnit: distanceUnitValue,
    encodedPolyline,
    features: isEmpty(selectedFeatureFilters)
      ? undefined
      : selectedFeatureFilters.map(feat => feat.id),
    inboundProgramBillTo,
    performanceRankingSearch: true,
    route: getRoute(componentProps),
  };
}

export const buildQueryChild = curry(
  (WrappedComponent, componentProps, queryProps) => {
    const { isRouteSearch, dealerDataTransformer } = componentProps;
    const { data, error: dealersError, loading, networkStatus } = queryProps;

    let dealers = isRouteSearch
      ? get(data, ['searchAssetRoute', 'dealers'])
      : get(data, ['searchAssetArea', 'dealers']);

    if (!!dealerDataTransformer && Array.isArray(dealers)) {
      dealers = dealers.map(dealerDataTransformer);
    }

    // FIXME: There is currently a bug in the apollo tooling that incorrectly sets
    // `loading` to true when `skip` is true. The workaround is to also check that
    // the query is not being skipped.
    // For more details: https://github.com/apollographql/react-apollo/issues/1869
    const querySkipped = skipQuery(componentProps);
    const dealersPending = !querySkipped && loading && networkStatus !== 6;

    return (
      <WrappedComponent
        {...componentProps}
        route={getRoute(componentProps)}
        dealers={querySkipped || dealersPending ? undefined : dealers}
        dealersError={dealersError}
        dealersPending={dealersPending}
      />
    );
  },
);

function buildWrappedComponentWithQuery(WrappedComponent, componentProps) {
  const { isRouteSearch } = componentProps;
  const shouldSkip = skipQuery(componentProps);

  return (
    <Query
      skip={shouldSkip}
      query={isRouteSearch ? searchAssetRouteGQL : searchAssetAreaGQL}
      variables={buildQueryVariables(componentProps)}
      pollInterval={shouldSkip ? 0 : pollInterval}
    >
      {buildQueryChild(WrappedComponent, componentProps)}
    </Query>
  );
}

export const buildConsumerChild = curry(
  (WrappedComponent, componentProps, consumerProps) => {
    const {
      countryCode,
      encodedPolyline,
      highlightedDealer,
      inboundProgramNumber,
      initialSelectedDealerId,
      isRouteSearch,
      locationSearches,
      selectedDealer,
      selectedFeatureFilters,
      shouldAutoSelectInitialDealer,
      unit,
      dealerDataTransformer,
    } = consumerProps;

    return buildWrappedComponentWithQuery(WrappedComponent, {
      ...componentProps,
      countryCode,
      encodedPolyline,
      highlightedDealer,
      inboundProgramNumber,
      initialSelectedDealerId,
      isRouteSearch,
      locationSearches,
      selectedDealer,
      selectedFeatureFilters,
      shouldAutoSelectInitialDealer,
      unit,
      dealerDataTransformer,
    });
  },
);

const withDealers = WrappedComponent => componentProps => (
  <DealerLocatorContext.Consumer>
    {buildConsumerChild(WrappedComponent, componentProps)}
  </DealerLocatorContext.Consumer>
);

const withExtraData = WrappedComponent =>
  compose(
    withProximityFilters,
    withDealers,
  )(WrappedComponent);

export default withExtraData;



// WEBPACK FOOTER //
// ./src/compositions/DealerLocator/withDealers/index.js